import React, { Component, Fragment, Suspense } from 'react';
import Media from 'react-media';
import PropTypes, { object, number, func, string, array, bool } from 'prop-types';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { Route, Switch, withRouter } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import map from 'lodash/map';
import merge from 'lodash/merge';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import download from 'downloadjs';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Snackbar from '@material-ui/core/Snackbar';
import Icon from '@material-ui/core/Icon';
import { withTheme } from '@material-ui/core/styles';
import queryString from 'query-string';
import compareVersions from 'compare-versions';
import amplitude from 'amplitude-js';
import { Amplitude } from '@amplitude/react-amplitude';
import ReactTooltip from 'react-tooltip';

import { getConfiguration, getChangeLog, logLimitHit } from '../util/util';
import {
  unrollReport,
  getReportMetadata,
  generateReportUrl,
  getReport,
  getReportFrameState
} from '../util/reports';
import { checkIfLoggedIn } from '../util/user';
import { addNewSubscription } from '../util/subscription';
import {
  truncatedPrefix,
  truncatedSuffixPremium,
  truncatedSuffixNonPremium,
  getRandomTip
} from '../util/status';
import {
  getOptionsProfiles,
  getCurrentUserOptions,
  getUserOptionsByName,
  setCurrentProfile,
  deleteUserOptionsByName,
  getDefaultOptions,
  setUserOptions
} from '../util/options';
import * as actions from '../actions/global';
import {
  DIFFNOW_URL,
  IS_CURRENT_PROFILE,
  SUCCESS_MESSAGE_DELAY_MS,
  COMPARE_CLIPS,
  COMPARE_FILES,
  COMPARE_URLS
} from '../util/constants';
import {
  canUserCompare,
  comparisonLimit,
  examDiffProUrl,
  isPremium,
  toDateString
} from '../util/common';
import { runWhenIdle } from '../util/idle';
import { loadAdsScript, isAdsScriptLoaded } from '../util/ads';
import {
  areAdsInvisible,
  logAnalytics,
  getOS,
  canUseExamDiff,
  edpDownloadUrl
} from '../util/browser';
import {
  compareClips,
  compareFiles,
  compareUrls,
  executePostComparisonActions
} from '../util/compare';
import {
  bumpComparisonCounter,
  comparisonLimitReachedStatus,
  setUpComparisonCounter,
  comparisonsUsedStatus
} from '../util/comparisonCount';
import { reconnectSignalR } from '../util/signalr';
import ErrorBoundary from '../components/ErrorBoundary';
import Status from '../components/Status';
import ComparisonAnimation from '../components/comparison/ComparisonAnimation';
import OutputFrame from '../components/comparison/OutputFrame';
import AdContainer from '../components/AdContainer';
import AdBlockMessage from '../components/AdBlockMessage';
import Footer from '../components/Footer';
import LandingContent from '../components/LandingContent';
import ConsentFooter from '../components/ConsentFooter';
import OptionsProfileButton from '../components/users/OptionsProfileButton';
import LoginDialog from '../components/users/LoginDialog';
import SavedReportsDialog from '../components/users/SavedReportsDialog';
import RegisterConfirmDialog from '../components/users/RegisterConfirmDialog';
import OptionsDialog from '../components/dialogs/OptionsDialog';
import AboutDialog from '../components/dialogs/AboutDialog';
import WhyPremiumDialog from '../components/dialogs/WhyPremiumDialog';
import CheckoutDialog from '../components/dialogs/CheckoutDialog';
import ChangePasswordDialog from '../components/dialogs/ChangePasswordDialog';
import ChangePasswordForResetDialog from '../components/dialogs/ChangePasswordForResetDialog';
import ManagePremiumSubscriptionDialog from '../components/dialogs/ManagePremiumSubscriptionDialog';
import TermsOfServiceDialog from '../components/dialogs/TermsOfServiceDialog';
import ContactDialog from '../components/dialogs/ContactDialog';
import StoreResultsDialog from '../components/dialogs/StoreResultsDialog';
import ActivationDialog from '../components/dialogs/ActivationDialog';
import ChangeLogDialog from '../components/dialogs/ChangeLogDialog';

import TitleBar from './TitleBar';
import CompareClips from './CompareClips';

// Lazy-loaded: React Router only ever renders the one route matching the current URL, so these
// don't need to be in the initial bundle - CompareClips stays eager since it's the default page.
// lazyWithReload guards against a stale chunk hash (the user's tab was open across a deploy that
// changed this chunk's filename) by reloading the page once instead of surfacing a load error.
const lazyWithReload = importFn =>
  React.lazy(() =>
    importFn()
      .then(module => {
        sessionStorage.removeItem('chunk-reload-attempted');
        return module;
      })
      .catch(error => {
        if (!sessionStorage.getItem('chunk-reload-attempted')) {
          sessionStorage.setItem('chunk-reload-attempted', '1');
          window.location.reload();
        }
        throw error;
      })
  );

const CompareFiles = lazyWithReload(() => import('./CompareFiles'));
const CompareUrls = lazyWithReload(() => import('./CompareUrls'));
const CompareFolders = lazyWithReload(() => import('./CompareFolders'));
const Samples = lazyWithReload(() => import('./Samples'));
const CheckoutSuccess = lazyWithReload(() => import('../components/CheckoutSuccess'));

const DIFFNOW_SIGNALR_URL = `${DIFFNOW_URL}/signalr`;

// Optional plug-ins (Premium) that change pasted text before comparing; with any of them on,
// report lines no longer match the pasted lines, so the report can't be merged
const TEXT_CHANGING_PLUGINS = ['html', 'xmlf', 'xmlc', 'jsnf', 'jsnc', 'sort', 'inis', 'pe32'];

// ExamDiff Pro card in the side column, per tool
const sideCards = {
  'compare-clips': {
    title: 'ExamDiff Pro for Windows',
    text:
      'The desktop app that powers DiffNow. Compare files and folders on your PC with no size limits, merge changes, and edit in place.'
  },
  'compare-files': {
    title: 'Comparing files often?',
    text:
      'ExamDiff Pro compares files on your PC without uploading them, with no size limit, and lets you merge and edit as you go.'
  },
  'compare-urls': {
    title: 'ExamDiff Pro for Windows',
    text:
      'Compare web pages, local files and whole folders side by side, with syntax highlighting and merging.'
  }
};

function mapStateToProps(state) {
  return {
    nightModeEnabled: state.global.theme === 'dark',
    renderId: state.global.renderId,
    status: state.global.status,
    isComparing: state.global.isComparing,
    isTruncated: state.global.isTruncated,
    isReportReady: state.global.isReportReady,
    isStoredReport: state.global.isStoredReport,
    reportId: state.global.reportId,
    durationKey: state.global.durationKey,
    examDiffUri: state.global.examDiffUri,
    firstTitle: state.global.firstTitle,
    secondTitle: state.global.secondTitle,
    user: state.global.user,
    optionsProfiles: state.global.optionsProfiles,
    options: state.global.options,
    newOptions: state.global.newOptions,
    clientId: state.global.clientId,
    defaultOptions: state.global.defaultOptions,
    optionsLoaded: state.global.optionsLoaded,
    configuration: state.global.configuration,
    comparisonCount: state.global.comparisonCount,
    comparisonStartDate: state.global.comparisonStartDate,
    lastComparedTabIndex: state.global.lastComparedTabIndex,
    collapsedTabIndex: state.global.collapsedTabIndex,
    premiumSource: state.global.premiumSource
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onLoggedIn(user) {
      dispatch(actions.loggedIn(user));
    },
    onLoggedOut() {
      dispatch(actions.loggedOut());
    },
    onSetClipTitles(firstTitle, secondTitle) {
      dispatch(actions.setClipTitles(firstTitle, secondTitle));
    },
    onSetClips(firstClip, secondClip) {
      dispatch(actions.setClips(firstClip, secondClip));
    },
    onSetDocType(docType) {
      dispatch(actions.setDocType(docType));
    },
    onSetFileTitles(firstTitle, secondTitle) {
      dispatch(actions.setFileTitles(firstTitle, secondTitle));
    },
    onSetUrls(firstUrl, secondUrl) {
      dispatch(actions.setUrls(firstUrl, secondUrl));
    },
    onSetUrlTitles(firstTitle, secondTitle) {
      dispatch(actions.setUrlTitles(firstTitle, secondTitle));
    },
    onSubscribeUser() {
      dispatch(actions.subscribeUser());
    },
    onSetStatus(status) {
      dispatch(actions.setStatus(status));
    },
    onSetReportId(reportId) {
      dispatch(actions.setReport(reportId, undefined));
    },
    onSetExamDiffUri(examDiffUri) {
      dispatch(actions.setExamDiffUri(examDiffUri));
    },
    onSetComparisonCount(newComparisonCount, newComparisonStartDate) {
      dispatch(actions.setComparisonCount(newComparisonCount, newComparisonStartDate));
    },
    onSetLastComparedTabIndex(newLastComparedTabIndex) {
      dispatch(actions.setLastComparedTabIndex(newLastComparedTabIndex));
    },
    onSetCollapsedTabIndex(newCollapsedTabIndex) {
      dispatch(actions.setCollapsedTabIndex(newCollapsedTabIndex));
    },
    onSetClientId(clientId) {
      dispatch(actions.setClientId(clientId));
    },
    onSetOptionsProfiles(optionsProfiles) {
      dispatch(actions.setOptionsProfiles(optionsProfiles));
    },
    onSetOptions(options) {
      dispatch(actions.setOptions(options));
    },
    onSetNewOption(newOption) {
      dispatch(actions.setNewOption(newOption));
    },
    onSaveNewOptions() {
      dispatch(actions.saveNewOptions());
    },
    onResetNewOptions() {
      dispatch(actions.resetNewOptions());
    },
    onSetDefaultOptions(defaultOptions) {
      dispatch(actions.setDefaultOptions(defaultOptions));
    },
    onSetTruncatedStatus(truncatedStatus) {
      dispatch(actions.setTruncatedStatus(truncatedStatus));
    },
    onSetConfiguration(configuration) {
      dispatch(actions.setConfiguration(configuration));
    },
    onSetReport(reportId, durationKey, isStoredReport) {
      dispatch(actions.setReport(reportId, durationKey, isStoredReport));
    },
    onSetTheme(newTheme) {
      dispatch(actions.setTheme(newTheme));
    },
    onSetPremiumSource(premiumSource) {
      dispatch(actions.setPremiumSource(premiumSource));
    },
    onSetReportReady(isReportReady) {
      dispatch(actions.setReportReady(isReportReady));
    }
  };
}

export class App extends Component {
  static propTypes = {
    nightModeEnabled: bool,
    history: object,
    renderId: number, // eslint-disable-line react/no-unused-prop-types
    status: object,
    isComparing: bool,
    isTruncated: bool,
    isReportReady: bool,
    isStoredReport: bool,
    reportId: string,
    durationKey: string,
    examDiffUri: string,
    firstTitle: string,
    secondTitle: string,
    onLoggedIn: func,
    onLoggedOut: func,
    onSetClipTitles: func,
    onSetClips: func,
    onSetDocType: func,
    onSetFileTitles: func,
    onSetUrls: func,
    onSetUrlTitles: func,
    onSetExamDiffUri: func,
    onSubscribeUser: func,
    onSetStatus: func,
    onSetClientId: func,
    onSetOptionsProfiles: func,
    onSetOptions: func,
    onSetNewOption: func,
    onSaveNewOptions: func,
    onResetNewOptions: func,
    onSetDefaultOptions: func,
    onSetTruncatedStatus: func,
    onSetComparisonCount: func,
    onSetConfiguration: func,
    onSetCollapsedTabIndex: func,
    onSetReportId: func,
    onSetReport: func,
    location: object,
    onSetTheme: func,
    onSetPremiumSource: func,
    onSetReportReady: func,
    user: object,
    optionsProfiles: array,
    options: object,
    newOptions: object,
    defaultOptions: object,
    clientId: string,
    optionsLoaded: bool,
    configuration: object,
    comparisonCount: number,
    comparisonStartDate: PropTypes.instanceOf(Date),
    lastComparedTabIndex: number,
    collapsedTabIndex: number,
    premiumSource: string,
    theme: object
  };

  state = {
    loading: false,
    activationConfirmationMessage: '',
    changePasswordConfirmationKey: null,
    changePasswordEmailAddress: null,

    currentTab: 'compare-clips',

    areAdsInvisible: false,
    isConsentFooterDismissed: false,
    currentVersion: null,
    changeLog: [],

    lastComparisonPayload: null,
    undoStack: [],
    redoStack: [],

    isLoginDialogOpen: false,
    isRegisterConfirmDialogOpen: false,
    registerConfirmDialogEmail: '',
    isSavedReportsDialogOpen: false,
    isOptionsDialogOpen: false,
    isAboutDialogOpen: false,
    isWhyPremiumDialogOpen: false,
    isCheckoutDialogOpen: false,
    isChangePasswordDialogOpen: false,
    isManagePremiumSubscriptionDialogOpen: false,
    isTermsOfServiceDialogOpen: false,
    isContactDialogOpen: false,
    isStoreResultsDialogOpen: false,
    isActivationDialogOpen: false,
    isChangePasswordForResetDialogOpen: false,
    isChangeLogDialogOpen: false,
    showSubscribedToast: false
  };

  componentDidMount() {
    this.onAppStart();
    window.addEventListener('message', this.onIframeMessage);
    this.stopHistoryListener = this.props.history.listen(this.onLocationChange);
    if (canUseExamDiff()) {
      edpDownloadUrl('diffnow-side')
        .then(url => this.setState({ edpDownloadUrl: url }))
        .catch(() => null);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Report bar buttons come and go (Undo, Redo, Re-compare); their tooltips need a rescan
    if (
      prevProps.reportId !== this.props.reportId ||
      prevState.undoStack.length !== this.state.undoStack.length ||
      prevState.redoStack.length !== this.state.redoStack.length
    ) {
      ReactTooltip.rebuild();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onIframeMessage);
    this.stopHistoryListener();
  }

  onIframeMessage = e => {
    const iframe = document.getElementById('diff-report-frame');
    if (!iframe || e.source !== iframe.contentWindow) return;
    const d = e.data;
    if (!isObject(d)) return;
    if (d.type === 'merge') {
      this.applyMerge(d);
      return;
    }
    if (d.type === 'undo' || d.type === 'redo') {
      this.stepHistory(d.type);
      return;
    }
    const update = {};
    if (d.type === 'orientationChanged') update.hrz = d.vertical;
    else if (d.type === 'inspectorChanged') update.sli = d.enabled;
    else if (d.type === 'wordWrapChanged') update.uww = d.enabled;
    else return;
    const newOptions = Object.assign({}, this.props.options, update);
    this.props.onSetOptions(newOptions);
    if (this.props.user && this.props.user.sessionToken) {
      setUserOptions(this.props.user.sessionToken, newOptions, this.props.defaultOptions);
    }
  };

  onAppStart = async () => {
    const clientId = uuidv4();

    this.setUpConsentFooter();
    this.setUpTheme();
    this.checkForActivation();
    this.checkForChangePassword();
    // Deferred: not needed for the initial paint, and was previously the largest contentful
    // paint element on a first visit (the dialog auto-opens then) - runs after the browser is
    // idle so it doesn't compete with the compare UI for a spot in the critical rendering path.
    runWhenIdle(() => this.checkForChangeLog());
    // Prefetch only - jQuery + the SignalR client are ~40-50KB that don't need to be in the
    // critical first-load bundle for a visitor who hasn't started a comparison yet. onCompare/
    // onRecompare call setUpSignalR again (idempotent) as a fallback in case a comparison starts
    // before this idle-time prefetch finishes.
    runWhenIdle(() => this.setUpSignalR(clientId));

    const sessionToken = window.localStorage.getItem('diffNowSessionToken');
    if (sessionToken) {
      const loginResponse = await checkIfLoggedIn(sessionToken);
      this.onUserLogin(loginResponse.data.user, sessionToken);
    } else {
      this.onUserLogout(sessionToken, true);
    }

    const identify = new amplitude.Identify().setOnce('user_os', getOS());
    amplitude.getInstance().identify(identify);

    // Deferred so it queues behind browser.js's ReactGA.initialize() idle callback (registered
    // earlier, at module-import time) - otherwise this pageview would fire before GA is ready.
    runWhenIdle(() => logAnalytics());
    this.checkPath();
    this.setUpConfigurationVariables();
    this.props.onSetClientId(clientId);
    this.props.onSetStatus({
      message: getRandomTip(),
      type: 'tip'
    });

    setInterval(() => {
      this.setState({ areAdsInvisible: areAdsInvisible() });
    }, 1000);
  };

  setUpConfigurationVariables = async () => {
    const configurationData = await getConfiguration();
    this.props.onSetConfiguration(configurationData);
    setUpComparisonCounter(this.props.onSetComparisonCount, configurationData);
  };

  // jQuery + the SignalR client are only actually needed once a comparison starts (they drive the
  // live "Comparing... / Truncating..." status messages), so they're loaded on demand via dynamic
  // import instead of bundled into the app's main chunk for every visitor. Idempotent (guarded by
  // signalRReady) so it's safe to call both from the idle-time prefetch in onAppStart AND, as a
  // fallback, from onCompare/onRecompare in case a comparison starts before that prefetch finishes.
  setUpSignalR = clientId => {
    if (!this.signalRReady) {
      this.signalRReady = (async () => {
        const { default: $ } = await import('jquery');
        window.jQuery = $;
        await import('../lib/jquery.signalR.min.js');
        await import('../signalr/hubs.js');

        const connectionHub = $.connection.diffnowHub;

        connectionHub.client.sendMessage = (type, message) => {
          const {
            isTruncated,
            user,
            comparisonCount,
            configuration,
            onSetStatus,
            onSetTruncatedStatus,
            onSetReportReady
          } = this.props;

          if (message.includes('Truncating')) {
            onSetTruncatedStatus(true);
          } else if (message.includes('Comparing')) {
            onSetTruncatedStatus(false);
          }

          onSetStatus({
            message: decodeURIComponent(message),
            type: type.toLowerCase()
          });

          if (type === 'Success') {
            window.setTimeout(() => {
              onSetReportReady(true);
              onSetStatus({ message: null, type: null }); // Clear status first (this is important for some logic).

              if (isTruncated) {
                const truncatedSuffix = isPremium(user)
                  ? truncatedSuffixPremium
                  : truncatedSuffixNonPremium;
                onSetStatus({
                  message: `${truncatedPrefix} ${this.props.options.lcl} ${truncatedSuffix}`,
                  type: 'error'
                });
              } else {
                onSetStatus(
                  comparisonsUsedStatus(
                    comparisonCount,
                    user,
                    configuration,
                    this.state.areAdsInvisible
                  )
                );
              }
            }, SUCCESS_MESSAGE_DELAY_MS);
          }
        };

        $.connection.hub.url = DIFFNOW_SIGNALR_URL;
        $.connection.hub.qs = `sessionId=${clientId}`;
        $.connection.hub.start();
      })();
    }

    return this.signalRReady;
  };

  hideStatusAfterDelay = delay => {
    window.setTimeout(() => {
      this.props.onSetStatus({ message: null, type: null });
    }, delay);
  };

  setUpConsentFooter = () => {
    this.setState({
      isConsentFooterDismissed: JSON.parse(window.localStorage.getItem('diffNowFooterDismissed'))
    });
  };

  setUpTheme = () => {
    const theme = window.localStorage.getItem('diffNowTheme');
    this.props.onSetTheme(isNil(theme) ? 'light' : theme);
  };

  checkPath = () => {
    if (window.location.pathname === '/') {
      this.redirectToLastViewedPage();
    } else if (window.location.pathname.startsWith('/report')) {
      this.lastReportPath = window.location.pathname;
      const reportId = window.location.pathname.split('/')[2];
      this.openReport(reportId);
    } else if (window.location.pathname.startsWith('/terms-of-service')) {
      this.redirectToLastViewedPage();
      this.toggleDialog('isTermsOfServiceDialogOpen');
    }

    this.setState({ currentTab: window.location.pathname.replace('/', '') });
  };

  // Keeps the current tool in step with in-app navigation (logo, Back, Forward)
  onLocationChange = location => {
    if (location.pathname === '/') {
      this.redirectToLastViewedPage();
    } else if (location.pathname.startsWith('/report')) {
      this.lastReportPath = location.pathname;
    } else {
      this.setState({ currentTab: location.pathname.replace('/', '') });
    }
  };

  redirectToLastViewedPage = () => {
    const lastViewedTab = window.localStorage.getItem('diffNowLastViewedTab');

    if (lastViewedTab) {
      this.setState({ currentTab: lastViewedTab.replace('/', '') });
      this.props.history.replace(`${lastViewedTab}${window.location.search}`);
    } else {
      this.setState({ currentTab: 'compare-clips' });
      this.redirectToCompareClips();
    }
  };

  openReport = async reportShortUrl => {
    if (reportShortUrl) {
      try {
        const { reportId, durationKey } = await unrollReport(reportShortUrl);
        this.handleReportSuccess(reportId, durationKey, reportShortUrl);
      } catch (error) {
        this.redirectToCompareClips();
      }
    } else {
      this.redirectToCompareClips();
    }
  };

  handleReportSuccess = async (reportId, durationKey, reportShortUrl) => {
    if (reportId !== 'not found') {
      getReportMetadata(
        reportShortUrl,
        reportMetadata => {
          const creationTime = toDateString(reportMetadata['GenerationTimeString']);
          const expirationTimeString =
            reportMetadata['ExpirationTime'] === null
              ? ''
              : `It expires on ${toDateString(reportMetadata['ExpirationTimeString'])}.`;
          const reportTitle = reportMetadata['Title'];

          this.props.onSetReportReady(true);
          this.props.onSetReport(reportId, durationKey, true);
          this.props.onSetStatus({
            message: `Report "${reportTitle}" was generated on ${creationTime}. ${expirationTimeString}`,
            type: 'info'
          });
        },
        error => {
          this.props.onSetStatus({
            message: error,
            type: 'error'
          });
        }
      );
    } else {
      this.redirectToCompareClips();
    }
  };

  redirectToCompareClips = () => {
    this.props.history.replace(`/compare-clips${window.location.search}`);
  };

  checkForActivation = () => {
    const params = queryString.parse(window.location.search);
    let message = null;

    if (params.activated && JSON.parse(params.activated)) {
      message =
        'Congratulations! Your DiffNow account has been created. You can now log in with your email and password.';
    } else if (params.activated && !JSON.parse(params.activated)) {
      if (params.error) {
        message = `Error: ${
          params.error
        }. Please try again, or contact us if this problem persists`;
      } else {
        message =
          'A bad confirmation key was given. Please try again, or contact us if this problem persists.';
      }
    }

    if (message) {
      this.setState({
        activationConfirmationMessage: message,
        isActivationDialogOpen: true
      });
    }
  };

  checkForChangePassword = () => {
    const params = queryString.parse(window.location.search);

    if (params.changePassword && JSON.parse(params.changePassword)) {
      this.setState({
        isChangePasswordForResetDialogOpen: true,
        changePasswordEmailAddress: params.emailAddress,
        changePasswordConfirmationKey: params.confirmationKey
      });
    }
  };

  checkForChangeLog = () => {
    const storedCurrentVersion = window.localStorage.getItem('diffNowCurrentVersion');

    getChangeLog(changeLog => {
      const sortedVersions = changeLog.sort((changesA, changesB) =>
        compareVersions(changesB.version, changesA.version)
      );
      const newestVersion = sortedVersions[0].version;

      this.setState({
        currentVersion: newestVersion,
        changeLog: sortedVersions
      });

      if (!storedCurrentVersion) {
        // First visit: nothing is new to this user
        this.markChangeLogViewed(newestVersion);
      } else if (newestVersion !== storedCurrentVersion) {
        this.setState({
          isChangeLogDialogOpen: true
        });
      }
    }, console.error);
  };

  markChangeLogViewed = version => {
    window.localStorage.setItem('diffNowCurrentVersion', version);
  };

  /* USER HELPERS */

  onUserLogin = (user, sessionToken) => {
    if (user.IsLoggedIn) {
      this.props.onLoggedIn({
        email: user.EmailAddress,
        sessionToken: sessionToken,
        isPremium: user.IsPremium
      });

      amplitude.getInstance().setUserId(user.EmailAddress);
      amplitude.getInstance().setUserProperties({
        user_email: user.EmailAddress,
        user_is_premium: user.IsPremium
      });

      window.localStorage.setItem('diffNowSessionToken', sessionToken);

      this.loadUserOptions(sessionToken);

      // The ad script (and Google's Auto/Anchor ad it injects) can't be un-loaded once it's
      // running - if it was already loaded (user started this session as non-premium, or was
      // premium before but just logged into a premium account without a full page reload), a
      // reload is the only way to guarantee they actually stop seeing ads now that they're premium.
      if (user.IsPremium && isAdsScriptLoaded()) {
        window.location.reload();
      }
    } else {
      this.onUserLogout(sessionToken);
    }
  };

  onUserLogout = async sessionToken => {
    this.props.onLoggedOut();

    amplitude.getInstance().setUserId(null);
    amplitude.getInstance().clearUserProperties();

    // Loaded here (rather than a static tag in index.html) so premium users never load it at all.
    // Also covers logging out mid-session: a user who started premium correctly never loaded this,
    // so it needs to load now that they're no longer premium and ads should actually appear.
    loadAdsScript();

    getDefaultOptions(sessionToken, defaultOptions => {
      this.props.onSetDefaultOptions(defaultOptions);
      this.props.onSetOptions(defaultOptions);
      this.props.onSetOptionsProfiles([
        {
          options_name: 'Default Profile',
          current_profile: 1
        }
      ]);
    });
  };

  onSubscribe = (subscriptionToken, plan, source) => {
    const sessionToken = window.localStorage.getItem('diffNowSessionToken');
    addNewSubscription(
      sessionToken,
      subscriptionToken.id,
      plan,
      source,
      () => {
        this.props.onSubscribeUser();
        this.toggleDialog('isWhyPremiumDialogOpen');
        this.toggleDialog('isCheckoutDialogOpen');
      },
      console.error
    );
  };

  handleCloseSubscribedToast = () => {
    this.setState({ showSubscribedToast: false });
  };

  /* COMPARISON */

  onCompare = async (type, comparisonPayload) => {
    const { comparisonCount, user, configuration, onSetStatus } = this.props;
    // A new comparison starts a new merge history
    this.setState({ undoStack: [], redoStack: [] });

    await this.setUpSignalR(this.props.clientId);
    await reconnectSignalR();

    if (
      canUserCompare(
        comparisonCount,
        user,
        configuration,
        this.logEvent,
        this.state.areAdsInvisible
      )
    ) {
      this.startComparison(type, comparisonPayload);
    } else {
      const limit = comparisonLimit(user, configuration, this.state.areAdsInvisible);
      logLimitHit(user && user.sessionToken, comparisonCount, limit, this.state.areAdsInvisible);
      onSetStatus(comparisonLimitReachedStatus(user, configuration, this.state.areAdsInvisible));
    }
  };

  onRecompare = async () => {
    const {
      comparisonCount,
      user,
      configuration,
      onSetStatus,
      reportId,
      lastComparedTabIndex,
      options
    } = this.props;
    const { lastComparisonPayload } = this.state;

    if (reportId && lastComparisonPayload) {
      // The new report starts a new merge history
      this.setState({ undoStack: [], redoStack: [] });
      await this.setUpSignalR(this.props.clientId);
      await reconnectSignalR();
      if (
        canUserCompare(
          comparisonCount,
          user,
          configuration,
          this.logEvent,
          this.state.areAdsInvisible
        )
      ) {
        const newComparisonPayload = Object.assign({}, lastComparisonPayload, {
          optionsJson: options
        });
        this.startComparison(lastComparedTabIndex, newComparisonPayload);
      } else {
        const limit = comparisonLimit(user, configuration, this.state.areAdsInvisible);
        logLimitHit(user && user.sessionToken, comparisonCount, limit, this.state.areAdsInvisible);
        onSetStatus(comparisonLimitReachedStatus(user, configuration, this.state.areAdsInvisible));
      }
    }
  };

  // Merging in the report: only for pasted text, with no plug-in changing the text, since the
  // page must own exactly the text the report shows
  get canEditReport() {
    const { options, isStoredReport } = this.props;
    const { reportTab, lastComparisonPayload } = this.state;
    const page = this.getPages()[reportTab];
    const tool = page ? page.tool : reportTab;
    return (
      tool === 'compare-clips' &&
      !!lastComparisonPayload &&
      !isStoredReport &&
      !TEXT_CHANGING_PLUGINS.some(key => options && options[key])
    );
  }

  // The report has merged a difference in place; the same change goes into the page's texts,
  // from the line ranges (1-based) it sends for both sides. Nothing is compared again.
  applyMerge = ({ diff, dir, from, to }) => {
    if (!this.canEditReport) return;
    const { clip1, clip2 } = this.state.lastComparisonPayload;
    const texts = [clip1.split(/\r?\n/), clip2.split(/\r?\n/)];
    const source = texts[from.side - 1],
      target = texts[to.side - 1];
    target.splice(
      to.first - 1,
      to.count,
      ...source.slice(from.first - 1, from.first - 1 + from.count)
    );
    const after = { clip1: texts[0].join('\n'), clip2: texts[1].join('\n') };
    this.logEvent('MERGE_IN_REPORT', { dir });
    this.setState(prev => ({
      undoStack: [...prev.undoStack, { before: { clip1, clip2 }, after, diff, dir }],
      redoStack: []
    }));
    this.setTexts(after);
  };

  // Undo and redo: the page sets its texts back or forward and tells the report to do the same
  stepHistory = kind => {
    if (!this.canEditReport) return;
    const undo = kind === 'undo';
    const stack = undo ? this.state.undoStack : this.state.redoStack;
    if (!stack.length) return;
    const step = stack[stack.length - 1];
    this.setState(prev =>
      undo
        ? { undoStack: prev.undoStack.slice(0, -1), redoStack: [...prev.redoStack, step] }
        : { redoStack: prev.redoStack.slice(0, -1), undoStack: [...prev.undoStack, step] }
    );
    this.setTexts(undo ? step.before : step.after);
    this.postToReport(
      undo ? { type: 'undoMerge' } : { type: 'redoMerge', diff: step.diff, dir: step.dir }
    );
  };

  setTexts = ({ clip1, clip2 }) => {
    this.setState(prev => ({
      lastComparisonPayload: Object.assign({}, prev.lastComparisonPayload, { clip1, clip2 })
    }));
    this.props.onSetClips(clip1, clip2);
  };

  postToReport = message => {
    const frame = document.getElementById('diff-report-frame');
    if (frame && frame.contentWindow) {
      frame.contentWindow.postMessage(message, '*');
    }
  };

  startComparison = async (type, comparisonPayload) => {
    const {
      onSetClipTitles,
      onSetClips,
      onSetDocType,
      onSetFileTitles,
      onSetUrls,
      onSetUrlTitles,
      onSetReportReady
    } = this.props;

    this.setState({ lastComparisonPayload: comparisonPayload });

    if (type === COMPARE_CLIPS) {
      onSetClipTitles(comparisonPayload.title1, comparisonPayload.title2);
      onSetClips(comparisonPayload.clip1, comparisonPayload.clip2);
      onSetDocType(comparisonPayload.clipType);
    } else if (type === COMPARE_FILES) {
      onSetFileTitles(comparisonPayload.originalFileName1, comparisonPayload.originalFileName2);
    } else if (type === COMPARE_URLS) {
      const getTitleFromUrl = title => title.substring(title.lastIndexOf('/') + 1);

      onSetUrls(comparisonPayload.url1, comparisonPayload.url2);
      onSetUrlTitles(
        getTitleFromUrl(comparisonPayload.url1),
        getTitleFromUrl(comparisonPayload.url2)
      );
    }

    comparisonPayload.useQueue = true;
    comparisonPayload.adBlockDetected = areAdsInvisible();

    const compareFn = [compareClips, compareFiles, compareUrls][type];
    try {
      onSetReportReady(false);
      const { reportId, examDiffUri } = await compareFn(comparisonPayload);
      this.onCompareSuccess(type, reportId, examDiffUri);
    } catch (error) {
      this.onCompareError(error);
    }
  };

  onCompareSuccess = (type, reportId, examDiffUri) => {
    const { onSetReportId, onSetExamDiffUri } = this.props;

    onSetReportId(reportId);
    onSetExamDiffUri(examDiffUri);
    if (this.openEdpAfterCompare) {
      this.openEdpAfterCompare = false;
      window.open(examDiffUri, '_self');
    }
    // The tab whose Results switch shows this report
    this.setState(prev => ({ reportTab: prev.currentTab }));
    bumpComparisonCounter(this.props);
    executePostComparisonActions(this.props, type);
  };

  onCompareError = error => {
    this.props.onSetStatus({
      message: error.message,
      type: 'error'
    });
  };

  /* OPTIONS HELPERS */

  loadUserOptions = sessionToken => {
    getDefaultOptions(sessionToken, defaultOptions => {
      this.props.onSetDefaultOptions(defaultOptions);
      getOptionsProfiles(sessionToken, this.props.onSetOptionsProfiles);
      getCurrentUserOptions(sessionToken, userOptions => {
        this.props.onSetOptions(merge({}, defaultOptions, userOptions));
      });
    });
  };

  getSelectedOptionsProfiles = (optionsProfiles, selectedProfile) =>
    map(optionsProfiles, prevProfile => {
      if (prevProfile.options_name === selectedProfile) {
        return Object.assign({}, prevProfile, { current_profile: 1 });
      } else {
        if (prevProfile.current_profile === IS_CURRENT_PROFILE) {
          return Object.assign({}, prevProfile, { current_profile: 0 });
        } else {
          return prevProfile;
        }
      }
    });

  getDeletedOptionsProfiles = (optionsProfiles, deletedProfile) =>
    optionsProfiles.filter(optionsProfile => optionsProfile.options_name !== deletedProfile);

  onProfileSelect = profile => {
    setCurrentProfile(this.props.user.sessionToken, profile);
    getUserOptionsByName(this.props.user.sessionToken, profile, userOptions => {
      this.props.onSetOptions(merge({}, this.props.defaultOptions, userOptions));
      this.props.onSetOptionsProfiles(
        this.getSelectedOptionsProfiles(this.props.optionsProfiles, profile)
      );
    });
  };

  onProfileDelete = profile => {
    deleteUserOptionsByName(this.props.user.sessionToken, profile, deletedProfile => {
      setCurrentProfile(this.props.user.sessionToken, 'Default Profile');
      getUserOptionsByName(this.props.user.sessionToken, 'Default Profile', userOptions => {
        this.props.onSetOptions(merge({}, this.props.defaultOptions, userOptions));
        this.props.onResetNewOptions();
        this.props.onSetOptionsProfiles(
          this.getSelectedOptionsProfiles(
            this.getDeletedOptionsProfiles(this.props.optionsProfiles, deletedProfile),
            'Default Profile'
          )
        );
      });
    });
  };

  onProfileSaveAs = profile => {
    const newProfiles = this.props.optionsProfiles;
    newProfiles.push({
      options_name: profile,
      current_profile: 1
    });

    this.props.onSetOptionsProfiles(
      map(newProfiles, prevProfile => {
        if (prevProfile.options_name !== profile) {
          return Object.assign({}, prevProfile, { current_profile: 0 });
        } else {
          return prevProfile;
        }
      })
    );

    setCurrentProfile(this.props.user.sessionToken, profile);
  };

  toggleNightMode = () => {
    const newTheme = this.props.nightModeEnabled ? 'light' : 'dark';
    this.props.onSetTheme(newTheme);
    window.localStorage.setItem('diffNowTheme', newTheme);
  };

  // Tool and landing pages come from the server (App_Data/landing-pages.json): injected into the page as
  // window.dnPages, and also returned by getConfiguration (the only source with yarn start)
  getPages = () => {
    const configuration = this.props.configuration || {};
    return window.dnPages || configuration.pages || {};
  };

  getPage = (key = this.state.currentTab) => this.getPages()[key];

  getTitleContent = () => {
    const page = this.getPage();
    if (page) {
      return page.title;
    }
    // Same title as the server's for /samples (ReactClientTools.aspx.cs)
    return this.state.currentTab === 'samples'
      ? 'Sample Comparisons - Try DiffNow with Example Files | DiffNow'
      : 'DiffNow - Compare Files, URLs, and Clipboard Contents Online';
  };

  // Visible page heading per tool (search engines read it as the page's H1)
  getHeadingContent = () => {
    const page = this.getPage();
    return page ? page.heading : null;
  };

  // A tool's input pane; landing pages (e.g. /compare-code) reuse the pane of their tool
  renderTool = (tool, props) => {
    const inputPane = (Pane, source, premiumSource) => (
      <Amplitude>
        {({ logEvent }) => (
          <Pane
            {...props}
            onCompare={this.onCompare}
            onSetCollapsedTabIndex={this.props.onSetCollapsedTabIndex}
            logEvent={logEvent}
            openWhyPremiumDialog={() => {
              logEvent('OPEN_WHY_PREMIUM_DIALOG', { source });
              this.props.onSetPremiumSource(premiumSource);
              this.toggleDialog('isWhyPremiumDialogOpen');
            }}
          />
        )}
      </Amplitude>
    );

    switch (tool) {
      case 'compare-clips':
        return inputPane(CompareClips, 'TEXT_INPUT_PANE', 'inputpane-text');
      case 'compare-files':
        return inputPane(CompareFiles, 'FILE_INPUT_PANE', 'inputpane-files');
      case 'compare-urls':
        return inputPane(CompareUrls, 'URL_INPUT_PANE', 'inputpane-urls');
      case 'compare-folders':
        return inputPane(CompareFolders, 'FOLDER_INPUT_PANE', 'inputpane-folders');
      default:
        return null;
    }
  };

  renderLandingPage = props => {
    const page = this.getPage(props.match.params.page);
    return page ? this.renderTool(page.tool, props) : null;
  };

  // Landing pages built on a tool (key differs from the tool), for the footer links
  getLandingPageLinks = () =>
    Object.keys(this.getPages())
      .filter(key => this.getPages()[key].tool !== key)
      .map(key => ({ path: `/${key}`, text: this.getPages()[key].heading }));

  /* DIALOG HELPERS */

  toggleDialog = (dialogState, otherState) => {
    this.setState(prevState =>
      Object.assign({}, { [dialogState]: !prevState[dialogState] }, otherState)
    );
  };

  downloadReport = async () => {
    const { reportId, durationKey } = this.props;
    this.logEvent('CLICKED_DOWNLOAD');
    let html = await getReport(reportId, durationKey);
    html = html.replace(
      /var \$licenseWWAllowed = [^;]+;/,
      `var $licenseWWAllowed = ${isPremium(this.props.user)};`
    );
    const state = await getReportFrameState();
    if (state) {
      html = html.replace(/var \$wordWrap = [^;]+;/, `var $wordWrap = ${state.wordWrap};`);
      html = html.replace(
        /var \$orientation = [^;]+;/,
        `var $orientation = '${state.orientation}';`
      );
      html = html.replace(
        /var \$useLineInspector = [^;]+;/,
        `var $useLineInspector = ${state.useLineInspector};`
      );
    }
    download(html, `${reportId}.html`);
  };

  // Plain outlined button of the report bar; on narrow screens only its icon shows
  reportButton(label, icon, onClick, { compact, disabled, title } = {}) {
    const { nightModeEnabled } = this.props;
    return (
      <span data-tip={title || (compact ? label : null)} data-for="report-bar-tooltip">
        <Button
          variant="outlined"
          disabled={disabled}
          onClick={onClick}
          style={{
            textTransform: 'none',
            minHeight: 34,
            minWidth: 0,
            padding: compact ? '0 8px' : '0 12px',
            fontSize: 14,
            backgroundColor: nightModeEnabled ? 'transparent' : '#ffffff'
          }}
        >
          <Icon style={{ fontSize: 18, marginRight: compact ? 0 : 6 }}>{icon}</Icon>
          {!compact && label}
        </Button>
      </span>
    );
  }

  // Report bar: back to the inputs, page actions, and Open in ExamDiff Pro as the one promo
  get topBar() {
    const { reportId, durationKey, examDiffUri, user } = this.props;

    return (
      <Media query="(max-width: 799px)">
        {compact => (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              marginBottom: 12
            }}
          >
            <div style={{ flexGrow: 1 }} />
            <ReactTooltip
              id="report-bar-tooltip"
              place="bottom"
              effect="solid"
              multiline
              className="report-bar-tooltip"
            />
            {this.reportButton(
              'Options',
              'tune',
              () => {
                this.logEvent('OPEN_OPTIONS_DIALOG', { source: 'TOP_BAR' });
                this.toggleDialog('isOptionsDialogOpen');
              },
              { compact, disabled: !this.props.optionsLoaded }
            )}
            {this.canEditReport &&
              this.reportButton('Undo', 'undo', () => this.stepHistory('undo'), {
                compact,
                disabled: !this.state.undoStack.length,
                title: 'Undo the last change (Ctrl+Z)'
              })}
            {this.canEditReport &&
              this.reportButton('Redo', 'redo', () => this.stepHistory('redo'), {
                compact,
                disabled: !this.state.redoStack.length,
                title: 'Redo (Ctrl+Y)'
              })}
            {this.canEditReport &&
              this.state.undoStack.length > 0 &&
              this.reportButton('Re-compare', 'refresh', this.onRecompare, {
                compact,
                title:
                  'Compare the merged texts again, for exact differences and counts. Merges can no longer be undone afterwards.'
              })}
            {this.reportButton(
              'Save/Share',
              'share',
              () => this.toggleDialog('isStoreResultsDialogOpen'),
              {
                compact,
                disabled: user === null,
                title: user === null ? 'Log in to save and share reports' : null
              }
            )}
            {this.reportButton('Download', 'file_download', this.downloadReport, { compact })}
            {this.reportButton(
              'Full screen',
              'fullscreen',
              () => {
                this.logEvent('CLICKED_FULLSCREEN');
                window.open(generateReportUrl(reportId, durationKey));
              },
              { compact }
            )}
            {examDiffUri && canUseExamDiff() && (
              <Button
                onClick={() => {
                  this.logEvent('DIFFNOW_OPEN_IN_EDP');
                  // The server's copies of the texts predate the merges, so compare the merged
                  // texts first and open ExamDiff Pro with those (onCompareSuccess)
                  if (this.state.undoStack.length) {
                    this.openEdpAfterCompare = true;
                    this.onRecompare();
                    return;
                  }
                  window.open(examDiffUri, '_self');
                  // The browser stays silent when ExamDiff Pro isn't installed
                  const trialUrl =
                    this.state.edpDownloadUrl || examDiffProUrl('diffnow-open-in-edp');
                  this.props.onSetStatus({
                    message: `ExamDiff Pro didn't open? <a href="${trialUrl}">Download the free trial</a>.`,
                    type: 'info'
                  });
                }}
                data-tip={
                  this.state.undoStack.length
                    ? 'Compares the merged texts again, then opens them in ExamDiff Pro on this PC. Merges can no longer be undone afterwards.'
                    : 'Open these files in ExamDiff Pro on this PC, to edit and merge them'
                }
                data-for="report-bar-tooltip"
                style={{
                  backgroundColor: '#FFEE58',
                  color: '#222222',
                  textTransform: 'none',
                  minHeight: 34,
                  padding: '0 12px',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                Open in ExamDiff Pro
              </Button>
            )}
          </div>
        )}
      </Media>
    );
  }

  get toolRoutes() {
    return (
      <Suspense fallback={null}>
        <Switch>
          <Route path="/compare-clips" render={props => this.renderTool('compare-clips', props)} />
          <Route path="/compare-files" render={props => this.renderTool('compare-files', props)} />
          <Route path="/compare-urls" render={props => this.renderTool('compare-urls', props)} />
          <Route
            path="/checkout-success"
            render={props => (
              <Amplitude>
                {({ logEvent }) => (
                  <CheckoutSuccess
                    {...props}
                    onSubscribeUser={this.props.onSubscribeUser}
                    onShowSubscribedToast={() => this.setState({ showSubscribedToast: true })}
                    logEvent={logEvent}
                  />
                )}
              </Amplitude>
            )}
          />
          <Route
            path="/compare-folders"
            render={props => this.renderTool('compare-folders', props)}
          />
          <Route path="/samples" render={props => <Samples {...props} />} />
          <Route path="/:page" render={this.renderLandingPage} />
        </Switch>
      </Suspense>
    );
  }

  get showingReport() {
    const { pathname } = this.props.location || window.location;
    return !!this.props.reportId && pathname.startsWith('/report');
  }

  // The tool whose inputs Edit inputs returns to
  get inputsPath() {
    const tab = this.state.currentTab;
    if (tab && !tab.startsWith('report')) {
      return `/${tab}`;
    }
    return window.localStorage.getItem('diffNowLastViewedTab') || '/compare-clips';
  }

  editInputs = () => {
    this.logEvent('EDIT_INPUTS');
    this.props.history.push(this.inputsPath);
    window.scrollTo(0, 0);
  };

  backToResults = () => {
    this.logEvent('BACK_TO_RESULTS');
    this.props.history.push(this.lastReportPath || '/report');
  };

  // Inputs | Results switch in the tool heading; Results needs a report from this tool
  get resultsSwitch() {
    const { reportId, nightModeEnabled } = this.props;
    const { reportTab, currentTab } = this.state;
    const resultsAvailable = !!reportId && (!reportTab || reportTab === currentTab);
    const border = nightModeEnabled ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)';
    const text = nightModeEnabled ? '#ffffff' : 'rgba(0, 0, 0, 0.87)';
    const faint = nightModeEnabled ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)';
    const part = (label, pressed, onClick, disabled, first) => (
      <button
        type="button"
        aria-pressed={pressed}
        disabled={disabled}
        onClick={pressed ? undefined : onClick}
        style={{
          border: 0,
          borderLeft: first ? 0 : `1px solid ${border}`,
          padding: '0 14px',
          height: 32,
          fontSize: 14,
          fontFamily: 'inherit',
          cursor: disabled || pressed ? 'default' : 'pointer',
          backgroundColor: pressed ? '#1565C0' : 'transparent',
          color: pressed ? '#ffffff' : disabled ? faint : text
        }}
      >
        {label}
      </button>
    );
    return (
      <div
        role="group"
        aria-label="Show"
        style={{
          display: 'inline-flex',
          border: `1px solid ${border}`,
          borderRadius: 8,
          overflow: 'hidden'
        }}
      >
        {part('Inputs', !this.showingReport, this.editInputs, false, true)}
        {part('Results', this.showingReport, this.backToResults, !resultsAvailable)}
      </div>
    );
  }

  // Errors, results of actions and tips, right below the inputs or the report bar
  get statusLine() {
    return (
      <Status
        message={this.props.status.message}
        type={this.props.status.type}
        nightModeEnabled={this.props.nightModeEnabled}
        openWhyPremiumDialog={source => {
          this.props.onSetPremiumSource(source);
          this.logEvent('OPEN_WHY_PREMIUM_DIALOG', { source: 'STATUS_BAR' });
          this.toggleDialog('isWhyPremiumDialogOpen');
        }}
        openOptionsDialog={() => {
          this.logEvent('OPEN_OPTIONS_DIALOG', { source: 'STATUS_BAR' });
          this.toggleDialog('isOptionsDialogOpen');
        }}
        removeTip={() => this.props.onSetStatus({ message: null, type: null })}
      />
    );
  }

  // One comparison option changed outside the Options dialog
  setQuickOption = (option, value) => {
    const newOptions = Object.assign({}, this.props.options, { [option]: value });
    this.props.onSetOptions(newOptions);
    if (this.props.user && this.props.user.sessionToken) {
      setUserOptions(this.props.user.sessionToken, newOptions, this.props.defaultOptions);
    }
    this.logEvent('SET_QUICK_OPTION', { option, value });
    // Showing a report: compare again with the new option (after the options update lands)
    if (this.showingReport) {
      setTimeout(() => this.onRecompare(), 0);
    }
  };

  quickOption(option, label) {
    const options = this.props.options || {};
    return (
      <FormControlLabel
        style={{ margin: 0 }}
        control={
          <Checkbox
            color="primary"
            checked={!!options[option]}
            disabled={!this.props.optionsLoaded}
            onChange={e => this.setQuickOption(option, e.target.checked)}
            style={{ width: 36, height: 36 }}
          />
        }
        label={label}
      />
    );
  }

  // Page heading (the page's H1 for search engines), common options and the Options button
  get toolHeading() {
    const tool = this.getPage() ? this.getPage().tool : this.state.currentTab;
    const comparesText = ['compare-clips', 'compare-files', 'compare-urls'].includes(tool);
    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          margin: '0 0 12px'
        }}
      >
        {this.getHeadingContent() && (
          <h1
            style={{
              fontSize: 22,
              fontWeight: 500,
              margin: 0,
              color: this.props.nightModeEnabled ? '#ffffff' : 'rgba(0, 0, 0, 0.87)'
            }}
          >
            {this.getHeadingContent()}
          </h1>
        )}
        {this.resultsSwitch}
        <div style={{ flexGrow: 1 }} />
        {comparesText && this.quickOption('iwa', 'Ignore white space')}
        {comparesText && this.quickOption('ica', 'Ignore case')}
        {this.props.user && (
          <OptionsProfileButton
            user={this.props.user}
            optionsProfiles={this.props.optionsProfiles}
            onProfileSelect={this.onProfileSelect}
          />
        )}
        <Button
          variant="outlined"
          disabled={!this.props.optionsLoaded}
          onClick={() => {
            this.logEvent('OPEN_OPTIONS_DIALOG', { source: 'TOOL_HEADING' });
            this.toggleDialog('isOptionsDialogOpen');
          }}
          style={{ textTransform: 'none', minHeight: 36 }}
        >
          More options
        </Button>
      </div>
    );
  }

  // ExamDiff Pro and the ad beside the tool on wide screens, below it on narrow ones
  sideColumn(wide, hideAdContainer) {
    const { nightModeEnabled } = this.props;
    const tool = this.getPage() ? this.getPage().tool : this.state.currentTab;
    // These pages promote ExamDiff Pro in the page itself, so the side gets a tall ad only
    const tall = ['compare-folders', 'samples'].includes(tool);
    const card = sideCards[tool] || sideCards['compare-clips'];
    if (tall) {
      return (
        <div style={{ position: wide ? 'sticky' : 'static', top: 16 }}>
          <AdContainer
            key="tall"
            adSlotId="4808589960"
            hideAdContainer={hideAdContainer}
            height={600}
          />
        </div>
      );
    }
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          position: wide ? 'sticky' : 'static',
          top: 16
        }}
      >
        <Paper
          style={{
            padding: 16,
            borderTop: '4px solid #FFEE58',
            display: 'flex',
            flexDirection: 'column',
            gap: 10
          }}
        >
          <div
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: nightModeEnabled ? '#ffffff' : 'rgba(0, 0, 0, 0.87)'
            }}
          >
            {card.title}
          </div>
          <div
            style={{
              fontSize: 14,
              lineHeight: 1.45,
              color: nightModeEnabled ? '#cccccc' : '#4A5468'
            }}
          >
            {card.text}
          </div>
          <Button
            variant="raised"
            color="secondary"
            href={this.state.edpDownloadUrl || examDiffProUrl('diffnow-side')}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              this.logEvent('CLICKED_EDP_SIDE_CARD', { download: !!this.state.edpDownloadUrl })
            }
            style={{
              alignSelf: 'flex-start',
              backgroundColor: '#FFEE58',
              color: '#000000',
              textTransform: 'none',
              fontSize: 15
            }}
          >
            {this.state.edpDownloadUrl ? 'Download free trial' : 'Learn more'}
          </Button>
        </Paper>
        <AdContainer
          key="short"
          adSlotId="4808589960"
          hideAdContainer={hideAdContainer}
          height={250}
        />
      </div>
    );
  }

  get content() {
    const showAdBlockHiddenMessage = !isPremium(this.props.user) && this.state.areAdsInvisible;
    const hideAdContainer = isPremium(this.props.user) || this.state.areAdsInvisible;

    if (this.state.loading) {
      return null;
    } else {
      return (
        <ErrorBoundary
          openContactDialog={() => {
            this.toggleDialog('isContactDialogOpen');
          }}
          nightModeEnabled={this.props.nightModeEnabled}
        >
          <div style={{ margin: this.showingReport ? '12px 16px' : '16px 20px' }}>
            {showAdBlockHiddenMessage && (
              <Amplitude>
                {({ logEvent }) => (
                  <AdBlockMessage
                    nightModeEnabled={this.props.nightModeEnabled}
                    openWhyPremiumDialog={source => {
                      logEvent('OPEN_WHY_PREMIUM_DIALOG', { source: 'AD_BLOCK_MESSAGE' });
                      this.props.onSetPremiumSource(source);
                      this.toggleDialog('isWhyPremiumDialogOpen');
                    }}
                  />
                )}
              </Amplitude>
            )}
            {this.showingReport && this.toolHeading}
            {this.showingReport && this.topBar}
            {this.showingReport && this.statusLine}
            <Media query="(min-width: 1080px)">
              {wide => (
                <div
                  style={
                    this.showingReport
                      ? {}
                      : {
                          display: 'grid',
                          gridTemplateColumns: wide ? 'minmax(0, 1fr) 300px' : 'minmax(0, 1fr)',
                          gap: 20,
                          alignItems: 'start'
                        }
                  }
                >
                  <div style={{ minWidth: 0 }}>
                    {!this.showingReport && this.toolHeading}
                    {this.toolRoutes}
                    {!this.showingReport && this.statusLine}
                    {!this.showingReport && this.getPage() && this.getPage().intro && (
                      <LandingContent
                        page={this.getPage()}
                        nightModeEnabled={this.props.nightModeEnabled}
                      />
                    )}
                  </div>
                  {!this.showingReport && this.sideColumn(wide, hideAdContainer)}
                </div>
              )}
            </Media>
            {this.props.isComparing && <ComparisonAnimation />}
            {/* Kept loaded while the inputs are shown, so Back to results is instant */}
            <div style={{ display: this.showingReport ? 'block' : 'none' }}>
              <OutputFrame
                reportId={this.props.reportId}
                durationKey={this.props.durationKey}
                isReportReady={this.props.isReportReady}
                nightModeEnabled={this.props.nightModeEnabled}
                isPremium={isPremium(this.props.user)}
                options={this.props.options}
                isStoredReport={this.props.isStoredReport}
                editable={this.canEditReport}
              />
            </div>
            {this.showingReport && (
              <AdContainer adSlotId="9678020683" hideAdContainer={hideAdContainer} />
            )}
            <Footer
              landingPages={this.getLandingPageLinks()}
              openTermsOfServiceDialog={() => {
                this.toggleDialog('isTermsOfServiceDialogOpen');
              }}
              openContactDialog={() => {
                this.toggleDialog('isContactDialogOpen');
              }}
              openChangeLogDialog={() => {
                this.toggleDialog('isChangeLogDialogOpen');
              }}
            />
            {/* Room to scroll the page's end clear of Google's anchor ad */}
            {!hideAdContainer && <div style={{ height: 100 }} />}
          </div>
        </ErrorBoundary>
      );
    }
  }

  get dialogs() {
    if (!this.state.loading) {
      return (
        <div>
          <LoginDialog
            open={this.state.isLoginDialogOpen}
            configuration={this.props.configuration}
            onUserLogin={this.onUserLogin}
            closeDialog={() => this.toggleDialog('isLoginDialogOpen')}
            openRegisterConfirmDialog={email => {
              this.toggleDialog('isRegisterConfirmDialogOpen', {
                registerConfirmDialogEmail: email
              });
            }}
          />
          <RegisterConfirmDialog
            open={this.state.isRegisterConfirmDialogOpen}
            email={this.state.registerConfirmDialogEmail}
            closeDialog={() => {
              this.toggleDialog('isRegisterConfirmDialogOpen', { registerConfirmDialogEmail: '' });
            }}
          />
          <SavedReportsDialog
            open={this.state.isSavedReportsDialogOpen}
            onSetStatus={this.props.onSetStatus}
            sessionToken={this.props.user ? this.props.user.sessionToken : null}
            closeDialog={() => {
              this.toggleDialog('isSavedReportsDialogOpen');
            }}
          />
          <Amplitude>
            {({ logEvent }) => (
              <OptionsDialog
                isPremium={isPremium(this.props.user)}
                shouldPromptToRecompare={!!this.props.reportId}
                sessionToken={this.props.user ? this.props.user.sessionToken : null}
                options={this.props.options}
                newOptions={this.props.newOptions}
                defaultOptions={this.props.defaultOptions}
                optionsProfiles={this.props.optionsProfiles}
                nightModeEnabled={this.props.nightModeEnabled}
                onProfileSelect={this.onProfileSelect}
                onProfileDelete={this.onProfileDelete}
                onProfileSaveAs={this.onProfileSaveAs}
                setOptions={this.props.onSetOptions}
                setNewOption={this.props.onSetNewOption}
                onSaveNewOptions={this.props.onSaveNewOptions}
                onResetNewOptions={this.props.onResetNewOptions}
                onRecompare={this.onRecompare}
                open={this.state.isOptionsDialogOpen}
                openWhyPremiumDialog={() => {
                  logEvent('OPEN_WHY_PREMIUM_DIALOG', { source: 'OPTIONS_DIALOG' });
                  this.props.onSetPremiumSource('options-dialog');
                  this.toggleDialog('isWhyPremiumDialogOpen');
                }}
                closeDialog={() => {
                  this.toggleDialog('isOptionsDialogOpen');
                }}
              />
            )}
          </Amplitude>
          <Amplitude>
            {({ logEvent }) => (
              <AboutDialog
                open={this.state.isAboutDialogOpen}
                configuration={this.props.configuration}
                openWhyPremiumDialog={() => {
                  logEvent('OPEN_WHY_PREMIUM_DIALOG', { source: 'OPTIONS_DIALOG' });
                  this.props.onSetPremiumSource('about-dialog');
                  this.toggleDialog('isWhyPremiumDialogOpen');
                }}
                closeDialog={() => {
                  this.toggleDialog('isAboutDialogOpen');
                }}
              />
            )}
          </Amplitude>
          <WhyPremiumDialog
            open={this.state.isWhyPremiumDialogOpen}
            configuration={this.props.configuration}
            openCheckoutDialog={() => {
              this.toggleDialog('isCheckoutDialogOpen');
            }}
            openLoginDialog={() => {
              this.toggleDialog('isWhyPremiumDialogOpen');
              this.toggleDialog('isLoginDialogOpen');
            }}
            user={this.props.user}
            closeDialog={() => {
              this.toggleDialog('isWhyPremiumDialogOpen');
            }}
          />
          <CheckoutDialog
            open={this.state.isCheckoutDialogOpen}
            onSubscribe={this.onSubscribe}
            user={this.props.user}
            premiumSource={this.props.premiumSource}
            closeDialog={() => {
              this.toggleDialog('isCheckoutDialogOpen');
            }}
            openTermsOfService={() => {
              this.toggleDialog('isTermsOfServiceDialogOpen');
            }}
          />
          <ChangePasswordDialog
            open={this.state.isChangePasswordDialogOpen}
            closeDialog={() => {
              this.toggleDialog('isChangePasswordDialogOpen');
            }}
          />
          <ChangePasswordForResetDialog
            open={this.state.isChangePasswordForResetDialogOpen}
            emailAddress={this.state.changePasswordEmailAddress}
            confirmationKey={this.state.changePasswordConfirmationKey}
            closeDialog={() => {
              this.toggleDialog('isChangePasswordForResetDialogOpen');
            }}
          />
          <ManagePremiumSubscriptionDialog
            open={this.state.isManagePremiumSubscriptionDialogOpen}
            user={this.props.user}
            closeDialog={() => {
              this.toggleDialog('isManagePremiumSubscriptionDialogOpen');
            }}
          />
          <TermsOfServiceDialog
            open={this.state.isTermsOfServiceDialogOpen}
            closeDialog={() => {
              this.toggleDialog('isTermsOfServiceDialogOpen');
            }}
          />
          <ContactDialog
            open={this.state.isContactDialogOpen}
            closeDialog={() => {
              this.toggleDialog('isContactDialogOpen');
            }}
          />
          <StoreResultsDialog
            open={this.state.isStoreResultsDialogOpen}
            user={this.props.user}
            reportId={this.props.reportId}
            firstTitle={this.props.firstTitle}
            secondTitle={this.props.secondTitle}
            storeResults={this.storeResults}
            closeDialog={() => {
              this.toggleDialog('isStoreResultsDialogOpen');
            }}
          />
          <ActivationDialog
            open={this.state.isActivationDialogOpen}
            message={this.state.activationConfirmationMessage}
            closeDialog={() => {
              this.toggleDialog('isActivationDialogOpen');
            }}
          />
          <ChangeLogDialog
            open={this.state.isChangeLogDialogOpen}
            changeLog={this.state.changeLog}
            closeDialog={() => {
              this.toggleDialog('isChangeLogDialogOpen');
              this.markChangeLogViewed(this.state.currentVersion);
            }}
          />
          <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            open={this.state.showSubscribedToast}
            autoHideDuration={6000}
            onClose={this.handleCloseSubscribedToast}
            message={<span>You are now subscribed to DiffNow Premium!</span>}
            action={[
              <IconButton
                key="close"
                aria-label="Close"
                color="inherit"
                onClick={this.handleCloseSubscribedToast}
              >
                <Icon>close</Icon>
              </IconButton>
            ]}
          />
        </div>
      );
    }
    return null;
  }

  render() {
    return (
      <Fragment>
        <Helmet>
          <title>{this.getTitleContent()}</title>
        </Helmet>
        <Amplitude>
          {({ logEvent }) => (
            <Fragment>
              {/* Store logEvent for use in methods */}
              {(() => {
                this.logEvent = logEvent;
                return null;
              })()}

              <TitleBar
                user={this.props.user}
                optionsProfiles={this.props.optionsProfiles}
                activeTool={this.getPage() ? this.getPage().tool : this.state.currentTab}
                setCurrentTab={currentTab => this.setState({ currentTab })}
                openAboutDialog={() => this.toggleDialog('isAboutDialogOpen')}
                openChangeLogDialog={() => this.toggleDialog('isChangeLogDialogOpen')}
                openContactDialog={() => this.toggleDialog('isContactDialogOpen')}
                openTermsOfServiceDialog={() => this.toggleDialog('isTermsOfServiceDialogOpen')}
                onUserLogout={this.onUserLogout}
                nightModeEnabled={this.props.nightModeEnabled}
                toggleNightMode={this.toggleNightMode}
                openLoginDialog={() => this.toggleDialog('isLoginDialogOpen')}
                openSavedReportsDialog={() => this.toggleDialog('isSavedReportsDialogOpen')}
                openWhyPremiumDialog={source => {
                  logEvent('OPEN_WHY_PREMIUM_DIALOG', { source });
                  this.props.onSetPremiumSource('accountsbar-button');
                  this.toggleDialog('isWhyPremiumDialogOpen');
                }}
                openChangePasswordDialog={() => this.toggleDialog('isChangePasswordDialogOpen')}
                openManagePremiumSubscriptionDialog={() =>
                  this.toggleDialog('isManagePremiumSubscriptionDialogOpen')
                }
              />
              {this.content}
              {this.dialogs}
            </Fragment>
          )}
        </Amplitude>
        <ConsentFooter
          isDismissed={!!this.state.isConsentFooterDismissed}
          openTosDialog={() => this.toggleDialog('isTermsOfServiceDialogOpen')}
          dismiss={() => {
            window.localStorage.setItem('diffNowFooterDismissed', true);
            this.setState({ isConsentFooterDismissed: true });
          }}
        />
      </Fragment>
    );
  }
}

export default withTheme()(
  withRouter(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(App)
  )
);
